import {
  ExternalBankAccount,
  InternalAccount,
  PayoutDestinationType
} from "@wingspanhq/wingspan-api-sdk";
import { useMemo } from "react";
import { queriesExternalBankAccounts, queriesInternalAccounts } from "@query";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { selectorExternalBankAccountFullName } from "@selectors/selectorExternalBankAccountFullName";
import { selectorExternalBankAccountPayoutReady } from "@selectors/selectorExternalBankAccountPayoutReady";
import { selectorExternalBankAccountRtpReady } from "@selectors/selectorExternalBankAccountRtpReady";
import { selectorActiveBankingAccount } from "../../shared/selectors/selectorActiveBankingAccount";

export type PayoutMethod =
  | {
      type: "account";
      data: ExternalBankAccount;
      percentage?: number;
    }
  | {
      type: "wallet";
      data?: InternalAccount;
      percentage?: number;
    }
  | { type: "giftcard"; percentage?: number };

export const usePayoutMethods = () => {
  const payoutSettingsQuery = useQueryOwnPayoutSettings();
  const queryInternalAccounts =
    queriesInternalAccounts.listInternalAccounts.useQuery();
  const queryExternalBankAccounts =
    queriesExternalBankAccounts.listExternalBankAccounts.useQuery();

  const standardDestination = payoutSettingsQuery.data?.standardDestination;
  const instantDestination = payoutSettingsQuery.data?.instantDestination;
  const bankingAccount = queryInternalAccounts.data?.data?.find(
    selectorActiveBankingAccount
  );
  const externalBankAccounts = (
    queryExternalBankAccounts.data?.data || []
  ).filter(selectorExternalBankAccountPayoutReady);

  const standard = useMemo(() => {
    const standardPayoutMethods: PayoutMethod[] = [];
    const otherStandardPayoutMethods: PayoutMethod[] = [];

    let standardDestinationPercentage: number = 100;
    let standardWalletPercentage: number = 0;

    if (standardDestination?.type === PayoutDestinationType.GIFT_CARD) {
      standardPayoutMethods.push({
        type: "giftcard",
        percentage: 100
      });
    } else {
      const payoutAccount = externalBankAccounts.find(
        externalBankAccount =>
          externalBankAccount.id === standardDestination?.id
      );
      standardDestinationPercentage = standardDestination?.percentage || 0;
      standardWalletPercentage = 100 - standardDestinationPercentage;

      if (payoutAccount) {
        standardPayoutMethods.push({
          type: "account",
          data: payoutAccount,
          percentage: standardDestinationPercentage
        });
      }

      if (standardWalletPercentage > 0 && bankingAccount) {
        standardPayoutMethods.push({
          type: "wallet",
          data: bankingAccount,
          percentage: standardWalletPercentage
        });
      }
    }

    externalBankAccounts
      .filter(
        externalBankAccount =>
          externalBankAccount.id !== standardDestination?.id
      )
      .forEach(externalBankAccount => {
        otherStandardPayoutMethods.push({
          type: "account",
          data: externalBankAccount
        });
      });

    if (standardWalletPercentage === 0 && bankingAccount) {
      otherStandardPayoutMethods.push({
        type: "wallet",
        data: bankingAccount
      });
    }

    return {
      active: standardPayoutMethods,
      other: otherStandardPayoutMethods
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payoutSettingsQuery.dataUpdatedAt,
    queryInternalAccounts.dataUpdatedAt,
    queryExternalBankAccounts.dataUpdatedAt
  ]);

  const instant = useMemo(() => {
    const instantPayoutAccounts: PayoutMethod[] = [];
    const otherInstantPayoutAccounts: PayoutMethod[] = [];

    const instantDestinationPercentage = instantDestination?.percentage || 0;
    const instantWalletPercentage = 100 - instantDestinationPercentage;

    externalBankAccounts
      .filter(
        externalBankAccount =>
          externalBankAccount.id !== instantDestination?.id &&
          selectorExternalBankAccountRtpReady(externalBankAccount)
      )
      .forEach(externalBankAccount => {
        otherInstantPayoutAccounts.push({
          type: "account",
          data: externalBankAccount
        });
      });

    if (instantWalletPercentage === 100 && bankingAccount) {
      // only the wallet is active
      instantPayoutAccounts.push({
        type: "wallet",
        data: bankingAccount,
        percentage: instantWalletPercentage
      });
    } else {
      // add the non-wallet account to the list
      const instantPayoutAccount = externalBankAccounts.find(
        externalBankAccount => externalBankAccount.id === instantDestination?.id
      );
      if (instantPayoutAccount) {
        instantPayoutAccounts.push({
          type: "account",
          data: instantPayoutAccount,
          percentage: instantDestinationPercentage
        });
      }

      if (bankingAccount) {
        if (instantWalletPercentage > 0) {
          instantPayoutAccounts.push({
            type: "wallet",
            data: bankingAccount,
            percentage: instantWalletPercentage
          });
        } else {
          otherInstantPayoutAccounts.push({
            type: "wallet",
            data: bankingAccount
          });
        }
      }
    }

    return {
      active: instantPayoutAccounts,
      other: otherInstantPayoutAccounts
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    payoutSettingsQuery.dataUpdatedAt,
    queryInternalAccounts.dataUpdatedAt,
    queryExternalBankAccounts.dataUpdatedAt
  ]);

  return {
    payoutPreferences: payoutSettingsQuery.data?.preferredPayoutSpeed,

    payoutMethods: {
      standard,
      instant
    },

    queries: {
      payoutSettingsQuery,
      queryInternalAccounts,
      queryExternalBankAccounts
    }
  };
};

export const getPayoutMethodName = (payoutMethod: PayoutMethod) =>
  payoutMethod.type === "account"
    ? selectorExternalBankAccountFullName(payoutMethod.data)
    : payoutMethod.type === "giftcard"
      ? "Gift Card"
      : `Wingspan Wallet${
          payoutMethod.data
            ? ` (${payoutMethod.data.numbers?.account?.slice(-4)})`
            : ""
        }`;
